"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import {
  CalendarDays,
  ChevronRight,
  LogOut,
  Contact,
  FileText,
  Globe,
  Inbox,
  KeyRound,
  LayoutDashboard,
  Lightbulb,
  Lock,
  type LucideIcon,
  Megaphone,
  Network,
  Scale,
  ShieldCheck,
  Upload,
  UserCheck,
  Users, HandHeart, ListChecks, CalendarRange } from "lucide-react";
import { LeafTag, Sprig } from "./qy";
import { dayLine } from "./qy/season";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar
} from "./ui/sidebar";
import { TooltipProvider } from "./ui/tooltip";
import { POLICY_READ_ROLES } from "../lib/policy-api";
import { reasonText } from "../lib/api";
import { useSession } from "../lib/session";
import { requestWorkspaceLeave, useWorkspaceHistoryGuard } from "./desk/unsaved-work";

// 桌面端负责「配」：起草、排版、发起。审查、放行、分诊在手机端（ADR-0020）。
// 每一条都写清需要哪个职责，没有职责的人看到的是说明，不是一个点不动的按钮。
export const ROLE_LABEL: Record<string, string> = {
  CONTENT_AUTHOR: "内容起草",
  CONTENT_EDITOR: "内容查看",
  PRIVACY_CONFIDENTIALITY_REVIEWER: "保密与公开性审查",
  CHANNEL_PUBLISHER: "通知与门户放行",
  PARTICIPATION_REVIEWER: "建言处置",
  PROFILE_CASE_HANDLER: "入会与资料经办",
  ACTIVITY_COORDINATOR: "活动组织",
  ACCESS_ADMIN: "授权管理",
  ORGANIZATION_ADMIN: "组织管理",
  GRASSROOTS_INFO_COORDINATOR: "基层联络",
  SOCIAL_SERVICE_COORDINATOR: "社会服务统计",
  AUDITOR: "审计查看"
};

export function roleLabel(roleCode: string): string {
  return ROLE_LABEL[roleCode] ?? roleCode;
}

/** 业务块（青榆 · 长天，DESIGN.md §8）：天青、杏、沙金、青榆、樱与藤。 */
export type Lane = "org" | "meet" | "clue" | "public" | "charity" | "learning";

export type Section = {
  href: string;
  label: string;
  hint: string;
  role: string | null;
  /** Any one of these effective roles admits the navigation item. */
  roles?: readonly string[];
  newTab?: boolean;
  icon: LucideIcon;
  /** 所属业务线：侧栏一枚小色块、顶栏底边一条线色（与小程序导航底边同一做法）。账号与授权不属于任何线。 */
  lane?: Lane;
};

// 桌面端负责「配」：起草、排版、发起。审查、放行、分诊在手机端（ADR-0020）。
// 每一条都写清需要哪个职责，没有职责的人看到的是说明，不是一个点不动的按钮。
const OVERVIEW: Section = {
  href: "/",
  label: "工作台",
  hint: "手头事项与近期活动",
  role: null,
  icon: LayoutDashboard
};

// 分组不是装饰：十四个台平铺在一列里，人得从头读到尾才找得到要去的那个。
export const GROUPS: Array<{ label: string; items: Section[] }> = [
  {
    label: "会务与履职",
    items: [
      {
        href: "/notices",
        lane: "meet",
        label: "通知发布",
        hint: "起草会务通知并送审；放行由另一人在手机端完成",
        role: "CONTENT_AUTHOR",
        icon: Megaphone
      },
      {
        href: "/portal",
        lane: "public",
        label: "门户内容",
        hint: "起草与排版对外内容并送保密审查",
        role: "CONTENT_AUTHOR",
        icon: Globe
      },
      {
        href: "/activities",
        lane: "meet",
        label: "活动管理",
        hint: "创建、发布与名册；签到在手机端",
        role: "ACTIVITY_COORDINATOR",
        icon: CalendarDays
      },
      { href: "/activities/ledger", lane: "meet", label: "活动台账", hint: "活动材料、结项与去重台账", role: "ACTIVITY_COORDINATOR", icon: ListChecks },
      {
        href: "/calls",
        lane: "clue",
        label: "材料征集",
        hint: "发起征集、截止收件；应征材料走建言收件链路",
        role: "PARTICIPATION_REVIEWER",
        icon: Inbox
      },
      {
        href: "/topics",
        lane: "clue",
        label: "课题与报送",
        hint: "立项、成稿与生成报送；标记报送在手机端",
        role: "PARTICIPATION_REVIEWER",
        icon: FileText
      },
      { href: "/manuscripts", lane: "clue", label: "稿件审核", hint: "待我审的稿件、文件与四类意见", role: "CONTENT_EDITOR", roles: ['CONTENT_EDITOR', 'PRIVACY_CONFIDENTIALITY_REVIEWER', 'PARTICIPATION_REVIEWER'], icon: FileText },
      {
        href: "/clues",
        lane: "clue",
        label: "建言受理",
        hint: "受理、研判与回执",
        role: "PARTICIPATION_REVIEWER",
        icon: Lightbulb
      },
      {
        href: "/duty-tasks",
        lane: "meet",
        label: "定向任务",
        hint: "向本组织发一项需要回应的事；看组织汇总与谁没回",
        role: "ACTIVITY_COORDINATOR",
        icon: ListChecks
      },
      {
        href: "/calendar",
        lane: "meet",
        label: "会务年历",
        hint: "录年度工作要点里的节点；会员首页「本季度重点」只从这里来",
        role: "ACTIVITY_COORDINATOR",
        icon: CalendarRange
      },
      {
        href: "/charity",
        lane: "charity",
        label: "社会服务统计",
        hint: "季度发布统计任务、看组织汇总；个人明细逐条打开并留痕",
        role: "SOCIAL_SERVICE_COORDINATOR",
        icon: HandHeart
      }
    ]
  },
  {
    label: "会员与组织",
    items: [
      {
        href: "/members",
        lane: "org",
        label: "会员档案",
        hint: "建档、登记会籍事实与履历、更正申报资料、合并重复档案",
        role: "PROFILE_CASE_HANDLER",
        icon: Users
      },
      {
        href: "/member-claims",
        lane: "org",
        label: "账号激活",
        hint: "认领没对上的工单、未认领名单",
        role: "PROFILE_CASE_HANDLER",
        icon: UserCheck
      },
      {
        href: "/contacts",
        lane: "org",
        label: "联系名册",
        hint: "查找授权范围内的会员联系方式，访问记录留存",
        role: "GRASSROOTS_INFO_COORDINATOR",
        icon: Contact
      },
      {
        href: "/member-imports",
        lane: "org",
        label: "会员数据导入",
        hint: "核对文件与字段，预检后确认入库，逐行结果可追溯",
        role: "PROFILE_CASE_HANDLER",
        icon: Upload
      },
      {
        href: "/organizations",
        lane: "org",
        label: "组织管理",
        hint: "组织树、新建下级、更名、调整上级与归档",
        role: "ORGANIZATION_ADMIN",
        icon: Network
      }
    ]
  },
  {
    label: "数据治理",
    items: [
      { href: "/cockpit", label: "领导驾驶舱", hint: "在新标签页查看业务快照与口径", role: "COCKPIT_VIEWER", roles: ["COCKPIT_VIEWER", "ORGANIZATION_ADMIN", "PROFILE_CASE_REVIEWER"], newTab: true, icon: LayoutDashboard },
      { href: "/verification", label: "核验任务", hint: "建批、分派与核验结果", role: "PROFILE_CASE_HANDLER", roles: ['PROFILE_CASE_HANDLER', 'PROFILE_CASE_REVIEWER', 'ORGANIZATION_ADMIN', 'GRASSROOTS_INFO_COORDINATOR'], icon: ListChecks, lane: "org" },
      { href: "/quality", label: "数据质量", hint: "问题、来源与处置记录", role: "PROFILE_CASE_HANDLER", roles: ['PROFILE_CASE_HANDLER', 'PROFILE_CASE_REVIEWER', 'ORGANIZATION_ADMIN', 'GRASSROOTS_INFO_COORDINATOR'], icon: ShieldCheck, lane: "org" },
      { href: "/indicators", label: "指标与来源", hint: "业务快照、口径与来源", role: "ORGANIZATION_ADMIN", roles: ['PROFILE_CASE_HANDLER', 'PROFILE_CASE_REVIEWER', 'ORGANIZATION_ADMIN', 'ACTIVITY_COORDINATOR', 'PARTICIPATION_REVIEWER', 'CONTENT_EDITOR', 'CHANNEL_PUBLISHER'], icon: LayoutDashboard, lane: "org" },
      { href: "/exports", label: "业务导出", hint: "申请、独立批准与受控下载", role: "PROFILE_CASE_HANDLER", roles: ['PROFILE_CASE_HANDLER', 'PROFILE_CASE_REVIEWER', 'ORGANIZATION_ADMIN', 'ACTIVITY_COORDINATOR', 'AUDITOR'], icon: FileText, lane: "org" },
      { href: "/cases", label: "变更案件", hint: "资料变更与办理时间线", role: "PROFILE_CASE_HANDLER", roles: ['PROFILE_CASE_HANDLER', 'PROFILE_CASE_REVIEWER'], icon: Inbox, lane: "org" },
      { href: "/policies", label: "生效规则", hint: "当前域规则、版本与未确认槽", role: "ORGANIZATION_ADMIN", roles: POLICY_READ_ROLES, icon: Scale, lane: "org" }
    ]
  },
  {
    label: "账号与授权",
    items: [
      {
        href: "/accounts",
        label: "账号管理",
        hint: "开户、停用、登录方式；账号本身不带权限",
        role: "ACCESS_ADMIN",
        icon: KeyRound
      },
      {
        href: "/grants",
        label: "授权管理",
        hint: "待授清单、按职务签发、复核与撤销；授的人不能自己复核",
        role: "ACCESS_ADMIN",
        icon: ShieldCheck
      },
      {
        href: "/audit",
        label: "审计查询",
        hint: "按授权范围核对操作、取证与对象历史；查询自动留痕",
        role: "AUDITOR", // 当前唯一持 AUDIT_READ 的职责模板。
        icon: ShieldCheck
      },
      {
        href: "/authority",
        label: "我的授权",
        hint: "看自己手里有什么权，把经办权转授给本组织的人",
        role: null,
        icon: Scale
      }
    ]
  }
];

const SECTIONS: Section[] = [OVERVIEW, ...GROUPS.flatMap((group) => group.items)];

// 开发态默认账号：与 bootstrap-staff-demo 的默认值一致，打开就能进，
// 不用每次翻文档抄口令（手机端 staff-login 早就是这么做的，这里补齐）。
// 只在开发构建里填：production 构建下这两个值编成空串，正式包不带预置口令。
// 预填而不是自动登录——这个合成域里有八个演示账号（授权管理、组织管理、
// 支部主委……），走查经常要换人登；自动登会把换号这条路堵死。
const DEV_DEFAULT_LOGIN =
  process.env.NODE_ENV === "production"
    ? { username: "", password: "" }
    : { username: "demo-staff", password: "demo-staff-2026" };

function NavItem({ section, active }: { section: Section; active: boolean }) {
  const Icon = section.icon;
  const { isMobile, setOpenMobile } = useSidebar();
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active} tooltip={section.hint}>
        <Link
          href={section.href}
          target={section.newTab ? "_blank" : undefined}
          rel={section.newTab ? "noopener noreferrer" : undefined}
          aria-current={active ? "page" : undefined}
          onClick={() => {
            if (isMobile) setOpenMobile(false);
          }}
        >
          {section.lane ? <span className={`console-lane-mark console-nav-leaf console-lane-mark--${section.lane} group-data-[collapsible=icon]:hidden`} aria-hidden /> : null}
          <Icon />
          <span>{section.label}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function ConsoleShell({ children }: { children: React.ReactNode }) {
  useWorkspaceHistoryGuard();
  const pathname = usePathname();
  const { phase, whoami, error, login, logout, hasRole } = useSession();
  const [username, setUsername] = useState(DEV_DEFAULT_LOGIN.username);
  const [password, setPassword] = useState(DEV_DEFAULT_LOGIN.password);
  const [working, setWorking] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [dateLabel, setDateLabel] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  useEffect(() => {
    try { setSidebarOpen(localStorage.getItem("ylmj-console-sidebar-open") !== "false"); } catch { /* 存储受限时仍可折叠。 */ }
  }, []);
  const changeSidebarOpen = (open: boolean) => {
    setSidebarOpen(open);
    try { localStorage.setItem("ylmj-console-sidebar-open", String(open)); } catch { /* 当前会话仍可使用。 */ }
  };
  useEffect(() => {
    // 顶栏日期用共享件的节气行，与小程序、门户同一个日子的写法。
    const update = () => setDateLabel(dayLine(new Date()));
    update();
    const timer = window.setInterval(update, 60_000);
    return () => window.clearInterval(timer);
  }, []);

  if (phase === "LOADING") {
    return (
      <main className="grid min-h-svh place-items-center bg-background">
        <p className="text-sm text-muted-foreground">正在读取当前身份…</p>
      </main>
    );
  }

  if (phase === "ANONYMOUS" || phase === "ERROR") {
    return (
      <main className="grid min-h-svh place-items-center bg-background px-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <p className="text-xs font-semibold tracking-wider text-primary">
              榆林民建 · 机关工作台
            </p>
            <CardTitle className="text-2xl">登录机关工作台</CardTitle>
            <CardDescription>
              在这里整理材料、起草通知与组织活动。使用工作人员账号进入你的授权范围。
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <form
              className="flex flex-col gap-4"
              onSubmit={async (event) => {
                event.preventDefault();
                setWorking(true);
                setFailure(null);
                try {
                  await login(username.trim(), password);
                  setPassword("");
                } catch (caught: unknown) {
                  setFailure(reasonText(caught));
                } finally {
                  setWorking(false);
                }
              }}
            >
              <div className="flex flex-col gap-2">
                <Label htmlFor="console-username">账号</Label>
                <Input
                  id="console-username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  autoComplete="username"
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="console-password">口令</Label>
                <Input
                  id="console-password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>
              <Button type="submit" disabled={working}>
                {working ? "正在登录…" : "登录"}
              </Button>
            </form>
            {failure || error ? (
              <p className="rounded-md bg-[var(--danger-soft)] px-4 py-3 text-sm text-[var(--danger)]">
                {failure ?? error}
              </p>
            ) : null}
            <p className="text-xs text-muted-foreground">仅供授权工作人员使用。</p>
          </CardContent>
        </Card>
      </main>
    );
  }

  const isActive = (href: string) => (href === "/" ? pathname === "/" : pathname.startsWith(href));
  const current = SECTIONS.find((section) => isActive(section.href));

  const visibleGroups = GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((section) => section.role === null || (section.roles ?? [section.role]).some(hasRole))
  })).filter((group) => group.items.length > 0);
  const currentGroup = GROUPS.find((group) => group.items.some((item) => isActive(item.href)));
  const synthetic =
    whoami?.domainId === "SYNTHETIC" || process.env.NEXT_PUBLIC_DATA_CLASSIFICATION === "SYNTHETIC";

  return (
    <TooltipProvider delayDuration={300}>
      <a className="skip-link" href="#main-content">
        跳至工作区
      </a>
      <SidebarProvider
        className="console"
        open={sidebarOpen}
        onOpenChange={changeSidebarOpen}
        style={
          { "--sidebar-width": "232px", "--sidebar-width-icon": "56px" } as React.CSSProperties
        }
      >
        <Sidebar collapsible="icon">
          <SidebarHeader>
            <Link href="/" className="console-brand" aria-label="榆林民建机关工作台首页">
              <span className="console-brand__mark" aria-hidden>
                <Sprig />
              </span>
              <span className="console-brand__copy">
                <strong>榆林民建</strong>
                <span>会务与履职工作台</span>
              </span>
            </Link>
          </SidebarHeader>
          <SidebarContent>
            <nav aria-label="工作台导航">
              <SidebarGroup>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <NavItem section={OVERVIEW} active={isActive(OVERVIEW.href)} />
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
              {visibleGroups.map((group) => (
                <SidebarGroup key={group.label}>
                  <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {group.items.map((section) => (
                        <NavItem
                          key={section.href}
                          section={section}
                          active={isActive(section.href)}
                        />
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              ))}
            </nav>
          </SidebarContent>
          <SidebarFooter>
            <div className="console-identity">
              <span className="console-avatar" aria-hidden>
                {whoami?.displayName?.slice(0, 1) ?? "人"}
              </span>
              <div className="console-identity__copy">
                <strong title={whoami?.displayName}>{whoami?.displayName}</strong>
                <Link href="/authority">查看我的授权</Link>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="console-signout justify-start"
              onClick={() => requestWorkspaceLeave(() => void logout())}
              aria-label="退出登录"
            >
              <LogOut className="size-4" />
              <span className="group-data-[collapsible=icon]:hidden">退出登录</span>
            </Button>
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>
        <SidebarInset>
          <header className={`console-topbar${current?.lane ? ` console-topbar--lane-${current.lane}` : ""}`}>
            <div className="console-topbar__path">
              <SidebarTrigger aria-label="展开或收起导航" />
              {currentGroup ? (
                <>
                  <span className="console-topbar__group">{currentGroup.label}</span>
                  <ChevronRight className="console-topbar__group size-3" aria-hidden />
                </>
              ) : null}
              <span>{current?.label ?? "机关工作台"}</span>
            </div>
            <div className="console-topbar__meta">
              <Sprig className="console-topbar__sprig" />
              {synthetic ? <LeafTag tone="sand" className="console-synthetic">合成演示数据</LeafTag> : null}
              <Link href="/authority" className="console-scope">
                <ShieldCheck className="size-3.5" aria-hidden />
                授权范围内
              </Link>
              <time>{dateLabel}</time>
            </div>
          </header>
          <div id="main-content" tabIndex={-1}>
            {children}
          </div>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  );
}

// 没有对应职责时不渲染表单：把一个永远会被服务端拒绝的表单摆在那里，
// 只会让人白填一遍。
export function RequireRole({
  roleCode,
  children
}: {
  roleCode: string;
  children: React.ReactNode;
}) {
  const { hasRole } = useSession();
  if (hasRole(roleCode)) return <>{children}</>;
  return (
    <div className="mx-auto w-full max-w-[var(--d-content-max)] px-10 py-9">
      <Card className="mx-auto max-w-lg">
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <span className="grid size-10 place-items-center rounded-[var(--r-inner)] bg-muted">
            <Lock className="size-4 text-muted-foreground" aria-hidden />
          </span>
          <p className="text-base font-semibold">当前职责不包含这一台</p>
          <p className="max-w-[42ch] text-sm leading-relaxed text-muted-foreground">
            这一台需要「{roleLabel(roleCode)}」职责。授权由授权管理员按范围签发，
            不能自助开通——在「我的授权」里看得到你手里有哪些。
          </p>
          <Button asChild variant="outline" size="sm" className="mt-1">
            <Link href="/authority">看我的授权</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
